import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { Close, CloudUpload } from '@material-ui/icons'
import React, { useEffect, useRef, useState } from 'react'
import { COLORS } from '../../constants'

export const useStyles = makeStyles(() => ({
  imageContainer: {
    position: 'relative',
    width: '100%',
    height: '232px',
  },
  image: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    borderRadius: 4,
  },
  deleteButton: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#fff',
    zIndex: 2,
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  uploadInput: {
    width: '100%',
    cursor: 'pointer',
    padding: 10,
    display: 'flex',
    marginTop: 10,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    border: '1px solid whitesmoke',
  },
}))

export interface GeImageUploaderProps {
  imageSrc: string | null
  label: string
  onUpload: (files: FileList | null) => void
  onDelete: () => void
  isUploading: boolean
  dialogState: 'open' | 'closed'
  showHeader?: boolean
}

export default function GenImageUploader({
  imageSrc,
  label,
  onUpload,
  onDelete,
  isUploading = false,
  dialogState,
  showHeader = true,
}: GeImageUploaderProps) {
  const classes = useStyles()
  const [isSrcLoading, setIsSrcLoading] = useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const imgRef = useRef<HTMLImageElement>(null)

  // Reset states when dialog opens or image changes
  useEffect(() => {
    if (dialogState === 'open') {
      setHasLoadError(false)
      setIsSrcLoading(!!imageSrc)
    }
  }, [imageSrc, dialogState])

  // Handle cached or immediate load
  useEffect(() => {
    const imgEl = imgRef.current
    if (imgEl && imgEl.complete) {
      if (imgEl.naturalWidth > 0) {
        setIsSrcLoading(false)
      } else {
        setHasLoadError(true)
        setIsSrcLoading(false)
      }
    }
  }, [imageSrc, dialogState])

  // Clear loader when parent upload ends
  useEffect(() => {
    if (!isUploading) {
      setIsSrcLoading(false)
    }
  }, [isUploading])

  const isLoading = isUploading || isSrcLoading

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {showHeader && (
        <Typography align="center" variant="subtitle1">
          {label}
        </Typography>
      )}

      <Box
        mt={showHeader ? 1 : 0}
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{ gap: 10 }}
      >
        {isLoading && (
          <Box
            width={200}
            height={150}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={40} />
          </Box>
        )}

        {!isLoading && hasLoadError && (
          <Typography variant="subtitle1" style={{ color: COLORS.coolTeal }}>
            Failed to load {label.toLowerCase()}
          </Typography>
        )}

        {imageSrc && !hasLoadError && !isLoading ? (
          <Box className={classes.imageContainer}>
            <IconButton
              size="small"
              className={classes.deleteButton}
              onClick={() => setConfirmOpen(true)}
            >
              <Close fontSize="small" />
            </IconButton>

            <img
              ref={imgRef}
              key={`${dialogState}-${imageSrc}`}
              src={imageSrc}
              className={classes.image}
              alt={label}
              style={{ display: isLoading ? 'none' : 'block' }}
              onLoad={() => setIsSrcLoading(false)}
              onError={() => {
                setHasLoadError(true)
                setIsSrcLoading(false)
              }}
            />
          </Box>
        ) : (
          <Typography variant="subtitle1" style={{ color: COLORS.coolTeal }}>
            {isUploading
              ? `Uploading ${label.toLowerCase()}...`
              : isSrcLoading
              ? `loading ${label.toLowerCase()}...`
              : `No ${label.toLowerCase()}`}
          </Typography>
        )}
      </Box>

      <label className={classes.uploadInput}>
        <CloudUpload fontSize="large" />

        <Typography variant="button" component="span">
          {imageSrc ? `Update ${label}` : `Upload ${label}`}
        </Typography>

        <input
          type="file"
          accept="image/jpg, image/jpeg, image/png, image/gif, image/svg+xml, image/webp, image/avif"
          onChange={e => {
            setIsSrcLoading(true)
            onUpload(e.target.files)
          }}
          style={{ display: 'none' }}
        />
      </label>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          are you sure you want to delete this {label.toLowerCase()}?
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              onDelete()
            }}
            style={{ color: COLORS.dangerRed }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
